/* eslint-disable no-mixed-spaces-and-tabs */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18next from "i18next";

import { setSupervisorRateLimitError } from "../../../features/user/user-slice";
import axios from "../../../utils/api-client";
import { SkillMatrix_JobRole } from "../../workers/store/manage-worker-types";
import {
	AdoptJobErrorStep,
	JobSkill,
	JobSliceStateTypes,
	Skill,
} from "./jobs-types";
interface createNewJobPayload {
	name: string;
	color: string;
	skills: Array<any>;
	workcenters: Array<any>;
	skillAndLevel: Array<any>;
}

export const createNewJob = createAsyncThunk(
	"manageJobs/createNewJob",
	async (
		payload: createNewJobPayload,
		{ fulfillWithValue, rejectWithValue, getState, dispatch }
	) => {
		const { name, color, skills, workcenters, skillAndLevel } = payload;

		// console.log("Skills : ", skills,payload);

		let workcenterIds = workcenters.reduce((acc, key) => {
			acc.push(key.id);
			return acc;
		}, []);

		let skillIds = skills.reduce((acc, key) => {
			acc.push(key.skillId);
			return acc;
		}, []);

		if (workcenterIds.length === 0)
			return rejectWithValue(i18next.t("jobs_error-select-workcenter"));

		if (color === "")
			return rejectWithValue(i18next.t("jobs_error-select-color"));
		// if (skillIds.length === 0)
		//     return rejectWithValue("Please select at least one skill.");

		workcenterIds = workcenterIds.toString();
		skillIds = skillIds.toString();

		const state = getState() as any;
		const userId = Number.parseInt(state.user.userData.id);
		const isJobSkillLevelMapping = state.user.userData.jobSkillLevelMapping;

		if (isJobSkillLevelMapping) {
			const noSkillLevels = skillAndLevel.filter(
				(skill: { minSkillLevelId?: number }) => !skill.minSkillLevelId
			);
			if (noSkillLevels.length > 0) {
				return rejectWithValue(i18next.t("jobs_error-select-skill-level"));
			}
		}

		const jobData = {
			name: name,
			hexColor: color,
			locationId: 1,
			workCenterIds: workcenterIds,
			skillIds,
			createdBy: userId,
			skillAndLevel,
		};

		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/Jobs/Create`,
				jobData
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				} else if (errorResponse.status === 404) {
					return rejectWithValue(error?.response?.data?.message);
				}
				return rejectWithValue(error?.response?.data?.message);
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

interface editJobPayload {
	id: number;
	name: string;
	color: string;
	skills: Array<any>;
	workcenters: Array<any>;
	skillAndLevel: Array<any>;
}

export const editJob = createAsyncThunk(
	"manageJobs/editJob",
	async (
		payload: editJobPayload,
		{ fulfillWithValue, rejectWithValue, getState, dispatch }
	) => {
		const { id, name, color, skills, workcenters, skillAndLevel } = payload;
		const jobId = id;
		const state = getState() as any;
		const allJobs = state.manageJobs.allJobs;
		let oldJobData = allJobs.filter(
			(job: SkillMatrix_JobRole) => job.id === jobId
		);
		let workcenterIds = workcenters.reduce((acc, key) => {
			acc.push(key.id);
			return acc;
		}, []);
		let skillIds = skills.reduce((acc, key) => {
			acc.push(key.skillId);
			return acc;
		}, []);
		if (workcenterIds.length === 0)
			return rejectWithValue(i18next.t("jobs_error-select-workcenter"));
		skillIds = skillIds.toString();
		workcenterIds = workcenterIds.toString();
		const userId = Number.parseInt(state.user.userData.id);

		const isJobSkillLevelMapping = state.user.userData.jobSkillLevelMapping;
		const isCrossSkillingOpen = state.user.userData.showCrossSkilling;

		if (isJobSkillLevelMapping) {
			const noSkillLevels = skillAndLevel.filter(
				(skill: { minSkillLevelId?: number }) => !skill.minSkillLevelId
			);
			if (noSkillLevels.length > 0 && isCrossSkillingOpen) {
				return rejectWithValue(i18next.t("jobs_error-select-skill-level"));
			}
		}

		const jobData = {
			id,
			name,
			hexColor: color,
			skillIds,
			locationId: 1,
			userId,
			createdBy: userId,
			workCenterIds: workcenterIds,
			skills,
			skillAndLevel,
		};
		oldJobData = oldJobData[0];
		const requestPayload = {
			...oldJobData,
			...jobData,
		};
		delete requestPayload.workcenterIds;
		delete requestPayload.dispatch;

		try {
			const editedJob = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/Jobs/Put`,
				requestPayload
			);

			if (editedJob.status === 204)
				return fulfillWithValue({ jobId, data: requestPayload });
		} catch (error: any) {
			try {
				const errorResponse = error?.response.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error?.response?.data?.message);
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

interface AdoptExistingJobPayload {
	jobId: number;
	workCenterIds: number[];
	name: string;
	color: string;
	skills: Array<any>;
	skillAndLevel: Array<any>;
	/**
	 * True when the adopted job's skills were prefilled into the form, so the
	 * form's skill list is authoritative. False when they could not be loaded
	 * (job not visible to the caller); the job's existing skills are then kept
	 * and the form's skills are added on top.
	 */
	skillsLoaded: boolean;
}

const toIdList = (value: unknown): string => {
	if (Array.isArray(value)) return value.join(",");
	if (typeof value === "string") return value;
	if (typeof value === "number") return value.toString();
	return "";
};

/**
 * "Use this Job Role" save chain:
 *   1. POST /Jobs/AddWorkCenters { jobId, workCenterIds } -> full job object
 *   2. PUT  /Jobs/Put with that object + name/color/skills from the form
 * Step 1 is skipped on retry when it already succeeded (its result is kept
 * in state.manageJobs.adoptJob.jobAfterWorkCenters).
 */
export const adoptExistingJob = createAsyncThunk(
	"manageJobs/adoptExistingJob",
	async (
		payload: AdoptExistingJobPayload,
		{ fulfillWithValue, rejectWithValue, getState, dispatch }
	) => {
		const { jobId, workCenterIds, name, color, skills, skillAndLevel } =
			payload;
		const state = getState() as any;
		const userId = Number.parseInt(state.user.userData.id);
		const isJobSkillLevelMapping = state.user.userData.jobSkillLevelMapping;
		const isCrossSkillingOpen = state.user.userData.showCrossSkilling;

		if (workCenterIds.length === 0) {
			return rejectWithValue({
				step: "wc",
				message: i18next.t("jobs_error-select-workcenter"),
			});
		}

		if (isJobSkillLevelMapping && isCrossSkillingOpen) {
			const noSkillLevels = skillAndLevel.filter(
				(skill: { minSkillLevelId?: number }) => !skill.minSkillLevelId
			);
			if (noSkillLevels.length > 0) {
				return rejectWithValue({
					step: "job",
					message: i18next.t("jobs_error-select-skill-level"),
				});
			}
		}

		const handleAxiosError = (error: any, step: AdoptJobErrorStep) => {
			const status = error?.response?.status;
			if (status === 429) {
				dispatch(setSupervisorRateLimitError());
			}
			return rejectWithValue({
				step,
				status,
				message:
					error?.response?.data?.message ??
					i18next.t(step === "wc" ? "jobs_save-error-wc" : "jobs_save-error-job"),
			});
		};

		// Step 1 - attach the caller's Work Centers (idempotent on the BE).
		let jobAfterWorkCenters: any =
			state.manageJobs.adoptJob.jobAfterWorkCenters;
		if (!jobAfterWorkCenters) {
			try {
				const res = await axios.post(
					`${process.env.REACT_APP_API_END_POINT}/Jobs/AddWorkCenters`,
					{ jobId, workCenterIds }
				);
				jobAfterWorkCenters = res.data;
			} catch (error: any) {
				return handleAxiosError(error, "wc");
			}
			dispatch(setAdoptJobStep({ jobAfterWorkCenters }));
		}

		// Step 2 - save name / color / skills on top of the returned job.
		// Work Centers always come from the response: it includes ones the
		// caller cannot see, so they must never be rebuilt from the form.
		const responseWorkCenterIds = toIdList(
			jobAfterWorkCenters?.workCenterIds ?? jobAfterWorkCenters?.workcenters
		);
		const existingSkills: any[] = Array.isArray(jobAfterWorkCenters?.skills)
			? jobAfterWorkCenters.skills
			: [];
		const mergedSkills = payload.skillsLoaded
			? skills
			: [
					...existingSkills,
					...skills.filter(
						(skill) =>
							!existingSkills.some(
								(existing) => existing.skillId === skill.skillId
							)
					),
			  ];
		const mergedSkillAndLevel = payload.skillsLoaded
			? skillAndLevel
			: [
					...existingSkills.map((skill) => ({
						skillId: skill.skillId,
						minSkillLevelId: isJobSkillLevelMapping
							? skill.minSkillLevelId ?? null
							: null,
					})),
					...skillAndLevel.filter(
						(skill) =>
							!existingSkills.some(
								(existing) => existing.skillId === skill.skillId
							)
					),
			  ];
		const skillIds = mergedSkills
			.map((skill) => skill.skillId)
			.filter((id) => id !== undefined && id !== null)
			.toString();

		const requestPayload = {
			...jobAfterWorkCenters,
			id: jobAfterWorkCenters?.id ?? jobAfterWorkCenters?.jobId ?? jobId,
			name,
			hexColor: color,
			skillIds,
			locationId: jobAfterWorkCenters?.locationId ?? 1,
			userId,
			createdBy: jobAfterWorkCenters?.createdBy ?? userId,
			workCenterIds: responseWorkCenterIds,
			skills: mergedSkills,
			skillAndLevel: mergedSkillAndLevel,
		};
		delete requestPayload.workcenters;
		delete requestPayload.jobId;

		try {
			await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/Jobs/Put`,
				requestPayload
			);
			return fulfillWithValue({ jobId: requestPayload.id, data: requestPayload });
		} catch (error: any) {
			return handleAxiosError(error, "job");
		}
	}
);

export const fetchJobsByUserId = createAsyncThunk(
	"manageJobs/fetchJobsByUserId",
	async (
		payload,
		{ fulfillWithValue, rejectWithValue, getState, dispatch }
	) => {
		const state = getState() as any;
		const userId = state.user.userData.id;
		try {
			const jobsResponse = axios.get(
				`${process.env.REACT_APP_API_END_POINT}/Jobs/GetJobsByUser?userid=${userId}`
			);

			const skillsResponse = axios.get(
				`${process.env.REACT_APP_API_END_POINT}/Skill/GetSkillsByUser`,
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: localStorage.getItem("token"),
					},
				}
			);

			const [jobs, skills] = await Promise.allSettled([
				jobsResponse,
				skillsResponse,
			]);

			let jobsList = [];
			let skillsList = [];
			if (jobs.status === "fulfilled" && skills.status === "fulfilled") {
				jobsList = jobs.value.data;
				skillsList = skills.value.data;
			}
			return fulfillWithValue({
				jobs: jobsList,
				skills: skillsList,
			});
		} catch (error: any) {
			//console.log(error)
			try {
				// return rejectWithValue(error.response.data.message);
				const errorResponse = error?.response.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error?.response?.data?.message);
			} catch (e) {
				return rejectWithValue("Error fetching jobs and skills");
			}
		}
	}
);

export const editSkill = createAsyncThunk(
	"manageJob/editSkill",
	async (
		payload: { name: string; id: string },
		{ dispatch, fulfillWithValue, rejectWithValue }
	) => {
		try {
			const res = await axios.put(
				`${process.env.REACT_APP_API_END_POINT}/Skill/Update`,
				payload
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			try {
				const errorResponse = error?.response.data;
				errorResponse.status = error?.response?.status;
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				} else if (errorResponse.status === 404) {
					return rejectWithValue(error?.response?.data?.message);
				}
				return rejectWithValue(error?.response?.data?.message);
			} catch {
				return rejectWithValue(i18next.t("common_error-occurred"));
			}
		}
	}
);

export const createNewSkill = createAsyncThunk(
	"manageJobs/createNewSkill",
	async (payload: any, { dispatch, fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/Skill/Create`,
				{ name: payload }
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			const errorResponse = error?.response.data;
			errorResponse.status = error?.response?.status;
			if (error.response.status === 404) {
				return rejectWithValue(error.response.data.message);
			} else if (errorResponse.status === 429) {
				dispatch(setSupervisorRateLimitError());
				return rejectWithValue(errorResponse);
			}
			return rejectWithValue(error.response.data.message);
		}
	}
);

export const fetchMaxWorkersByLocationJob = createAsyncThunk(
	"scheduler/fetchMaxWorkersByLocationJob",
	/**  @param payload {locationId: string; jobId: string; startDateTime: string; endDateTime: string, shiftSkills, includeWorkers?: boolean  } */
	async (payload: any, thunkAPI) => {
		// includeWorkers also returns the eligible Workers, for picking who
		// covers a Shift that does not exist yet (Direct Worker Assignment)
		const { includeWorkers, ...shiftParameters } = payload ?? {};
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/Shifts/GetMaxWorkersForShift${
					includeWorkers ? "?includeWorkers=true" : ""
				}`,
				shiftParameters
			);
			if (response.status === 200) {
				const data = response.data;
				// Without the flag the API answers a bare count, as before
				const isEnriched =
					!!data && typeof data === "object" && "eligibleCount" in data;
				return thunkAPI.fulfillWithValue({
					count: isEnriched ? data.eligibleCount : data,
					workers:
						isEnriched && Array.isArray(data.workers) ? data.workers : [],
				});
			}
			return thunkAPI.rejectWithValue(response.data.error);
		} catch (error: any) {
			const errorResponse = error?.response.data;
			errorResponse.status = error?.response?.status;
			if (errorResponse.status === 429) {
				thunkAPI.dispatch(setSupervisorRateLimitError());
				return thunkAPI.rejectWithValue(errorResponse);
			}
			// console.error(error);
			return thunkAPI.rejectWithValue(error);
		}
	}
);

export const getWorkersCountByShift = createAsyncThunk(
	"scheduler/getWorkersCountByShift",
	/**  @param payload {locationId: string; jobId: string; workcenterId:number; startDateTime: string; endDateTime: string, shiftSkills,shiftId:number  } */
	async (
		payload: {
			locationId: string;
			jobId: string;
			workcenterId: number;
			startDateTime: string;
			endDateTime: string;
			shiftSkills: any;
			shiftId: number;
			isPartialSignupEnabled: boolean;
		},
		thunkAPI
	) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/Shifts/GetMaxWorkersForShift`,
				payload
			);
			if (response.status === 200) {
				return thunkAPI.fulfillWithValue(
					Object.assign({}, payload, { count: response.data })
				);
			}
			return thunkAPI.rejectWithValue(response.data.error);
		} catch (error: any) {
			const errorResponse = error?.response.data;
			errorResponse.status = error?.response?.status;
			if (errorResponse.status === 429) {
				thunkAPI.dispatch(setSupervisorRateLimitError());
				return thunkAPI.rejectWithValue(errorResponse);
			}
			return thunkAPI.rejectWithValue(error);
		}
	}
);

export const ManageJobsSlice = createSlice({
	name: "manageJobs",
	initialState: {
		addJobDialog: {
			isOpen: false,
		},

		editJobDialog: {
			isOpen: false,
			job: undefined,
		},

		editSkillDialog: {
			isOpen: false,
			skill: undefined,
		},

		createSkillDialog: {
			isOpen: false,
		},

		allJobs: [],
		allSkills: [],

		fetchJobsByUserId: {
			status: "idle",
			errorMessage: "",
		},

		createNewJob: {
			status: "idle",
			errorMessage: "",
		},
		editJob: {
			status: "idle",
			errorMessage: "",
		},
		editSkill: {
			status: "idle",
			errorMessage: "",
		},
		createNewSkill: {
			status: "idle",
			errorMessage: "",
		},
		fetchMaxWorkersByLocationJob: {
			status: "idle",
			errorMessage: "",
			data: {},
			workers: [],
		},
		getWorkersCountByShift: {
			status: "idle",
			errorMessage: "",
			data: {},
		},
		jobDetailsDrawer: {
			isOpen: false,
			jobId: 0,
		},
		jobSkillMap: {},
		allSkillLevels: [],
		adoptJob: {
			status: "idle",
			errorStep: null,
			errorMessage: "",
			jobAfterWorkCenters: null,
		},
	} as JobSliceStateTypes,

	reducers: {
		openAddJobDialog: (state, action) => {
			state.addJobDialog.isOpen = true;
			state.createNewJob.status = "idle";
			state.createNewJob.errorMessage = "";
			state.editJob.status = "idle";
			state.editJob.errorMessage = "";
			state.adoptJob = {
				status: "idle",
				errorStep: null,
				errorMessage: "",
				jobAfterWorkCenters: null,
			};
		},
		closeAddJobDialog: (state) => {
			state.addJobDialog.isOpen = false;
			state.adoptJob = {
				status: "idle",
				errorStep: null,
				errorMessage: "",
				jobAfterWorkCenters: null,
			};
		},
		resetAdoptJob: (state) => {
			state.adoptJob = {
				status: "idle",
				errorStep: null,
				errorMessage: "",
				jobAfterWorkCenters: null,
			};
		},
		setAdoptJobStep: (state, action) => {
			state.adoptJob.status = "updating";
			state.adoptJob.jobAfterWorkCenters =
				action.payload?.jobAfterWorkCenters ?? null;
		},
		openEditJobDialog: (state, action) => {
			state.editJobDialog.isOpen = true;
			state.editJobDialog.job = action.payload;
			state.editJob.status = "idle";
			state.editJob.errorMessage = "";
			state.createNewJob.status = "idle";
			state.createNewJob.errorMessage = "";
		},
		openEditSkillDialog: (state, action) => {
			state.editSkillDialog.isOpen = true;
			state.editSkillDialog.skill = action.payload;
			state.editSkill.status = "idle";
			state.editSkill.errorMessage = "";
		},
		closeEditJobDialog: (state) => {
			state.editJobDialog.isOpen = false;
			state.editJobDialog.job = undefined;
		},
		closeEditSkillDialog: (state) => {
			state.editSkillDialog.isOpen = false;
			state.editSkillDialog.skill = undefined;
		},
		openCreateSkillDialog: (state) => {
			state.createSkillDialog.isOpen = true;
			state.createNewSkill.status = "idle";
			state.createNewSkill.errorMessage = "";
		},
		closeCreateSkillDialog: (state) => {
			state.createSkillDialog.isOpen = false;
			state.createNewSkill.status = "idle";
			state.createNewSkill.errorMessage = "";
		},
		resetCreateNewJob: (state) => {
			state.createNewJob.status = "idle";
			state.createNewJob.errorMessage = "";
		},
		resetCreateNewSkill: (state) => {
			state.createNewSkill.status = "idle";
			state.createNewSkill.errorMessage = "";
		},
		resetEditJob: (state) => {
			state.editJob.status = "idle";
			state.editJob.errorMessage = "";
		},
		resetEditSkill: (state) => {
			state.editSkill.status = "idle";
			state.editSkill.errorMessage = "";
			state.editSkillDialog.isOpen = false;
			state.editSkillDialog.skill = undefined;
		},
		resetMaxWorkersByLocationJob: (state) => {
			state.fetchMaxWorkersByLocationJob.status = "idle";
			state.fetchMaxWorkersByLocationJob.errorMessage = "";
			state.fetchMaxWorkersByLocationJob.data = {};
			state.fetchMaxWorkersByLocationJob.workers = [];
		},
		resetFetchJobsByUserId: (state) => {
			state.fetchJobsByUserId.status = "idle";
			state.fetchJobsByUserId.errorMessage = "";
			state.allJobs = [];
			state.allSkills = [];
			state.jobSkillMap = {};
		},
		resetWorkersCountByShift: (state) => {
			state.getWorkersCountByShift.status = "idle";
			state.getWorkersCountByShift.errorMessage = "";
			state.getWorkersCountByShift.data = {};
		},
		openJobDetailsDrawer: (state, action) => {
			state.jobDetailsDrawer.isOpen = true;
			state.jobDetailsDrawer.jobId = action.payload;
		},
		closeJobDetailsDrawer: (state) => {
			state.jobDetailsDrawer.isOpen = false;
			state.jobDetailsDrawer.jobId = 0;
		},
	},

	extraReducers: (builder) => {
		builder.addCase(createNewJob.pending, (state) => {
			state.createNewJob.status = "pending";
		});
		builder.addCase(createNewJob.fulfilled, (state, action) => {
			state.allJobs = [...state.allJobs, action.payload];
			state.addJobDialog.isOpen = false;
			state.createNewJob.status = "fulfilled";
		});
		builder.addCase(createNewJob.rejected, (state, action: any) => {
			if (action?.payload?.status === 429) {
				state.createNewJob.status = "error";
			} else {
				state.createNewJob.status = "error";
				state.createNewJob.errorMessage = action.payload ?? "An error occurred";
			}
		});
		builder.addCase(fetchJobsByUserId.pending, (state) => {
			state.fetchJobsByUserId.status = "pending";
		});
		builder.addCase(fetchJobsByUserId.fulfilled, (state, action) => {
			const { jobs, skills } = action.payload;
			state.allJobs = jobs;
			const allSkills = skills.map((skill: Skill) => ({
				...skill,
				isActive: !skill.isObsolete,
			}));

			state.allSkills = allSkills;
			state.jobSkillMap =
				jobs.length > 0
					? jobs?.reduce((acc: any, job: any) => {
							job.skills.forEach((skill: JobSkill) => {
								acc[`${job.id}-${skill.skillId}`] = skill.minSkillLevelId;
							});
							return acc;
					  }, {})
					: {};
			state.fetchJobsByUserId.status = "fulfilled";
		});
		builder.addCase(fetchJobsByUserId.rejected, (state, action: any) => {
			if (action?.payload?.status === 429) {
				state.fetchJobsByUserId.status = "error";
			} else {
				state.fetchJobsByUserId.status = "error";
				state.fetchJobsByUserId.errorMessage =
					action.payload ?? "An error occurred";
			}
		});
		builder.addCase(editJob.pending, (state) => {
			state.editJob.status = "pending";
		});
		builder.addCase(editJob.fulfilled, (state, action: any) => {
			const jobId = action.payload?.jobId;
			const newWorkerData = action.payload?.data;
			const index = state.allJobs.findIndex(
				(el: SkillMatrix_JobRole) => el.id == jobId
			);
			state.allJobs[index] = newWorkerData;
			state.editJobDialog.isOpen = false;
			state.editJob.status = "fulfilled";
		});
		builder.addCase(editJob.rejected, (state, action: any) => {
			if (action?.payload?.status === 429) {
				state.editJob.status = "error";
			} else {
				state.editJob.status = "error";
				state.editJob.errorMessage = action.payload ?? "An error occurred";
			}
		});
		builder.addCase(adoptExistingJob.pending, (state) => {
			state.adoptJob.status = state.adoptJob.jobAfterWorkCenters
				? "updating"
				: "addingWc";
			state.adoptJob.errorStep = null;
			state.adoptJob.errorMessage = "";
		});
		builder.addCase(adoptExistingJob.fulfilled, (state, action: any) => {
			const jobId = action.payload?.jobId;
			const updatedJob = action.payload?.data;
			const index = state.allJobs.findIndex(
				(el: SkillMatrix_JobRole) => el.id === jobId
			);
			if (index >= 0) {
				state.allJobs[index] = updatedJob;
			} else if (updatedJob) {
				state.allJobs = [...state.allJobs, updatedJob];
			}
			state.adoptJob.status = "fulfilled";
			state.adoptJob.jobAfterWorkCenters = null;
			state.addJobDialog.isOpen = false;
			state.editJobDialog.isOpen = false;
			state.editJobDialog.job = undefined;
			// Reuse the existing "edit successful" snackbar + grid refresh.
			state.editJob.status = "fulfilled";
			state.editJob.errorMessage = "";
		});
		builder.addCase(adoptExistingJob.rejected, (state, action: any) => {
			state.adoptJob.status = "error";
			state.adoptJob.errorStep = action.payload?.step ?? "wc";
			state.adoptJob.errorMessage =
				action.payload?.status === 429
					? ""
					: action.payload?.message ?? i18next.t("common_error-occurred");
		});
		builder.addCase(createNewSkill.pending, (state) => {
			state.createNewSkill.status = "pending";
		});
		builder.addCase(createNewSkill.fulfilled, (state, action) => {
			// The create endpoint returns the raw skill, so derive isActive the
			// same way fetchJobsByUserId does - otherwise a freshly created skill
			// lands in the list without the flag and reads as obsolete.
			state.allSkills = [
				...state.allSkills,
				{ ...action.payload, isActive: !action.payload?.isObsolete },
			];
			state.createSkillDialog.isOpen = false;
			state.createNewSkill.status = "fulfilled";
		});
		builder.addCase(createNewSkill.rejected, (state, action: any) => {
			if (action?.payload?.status === 429) {
				state.createNewSkill.status = "error";
			} else {
				state.createNewSkill.status = "error";
				state.createNewSkill.errorMessage =
					action.payload ?? "An error occurred";
			}
		});
		builder.addCase(editSkill.pending, (state) => {
			state.editSkill.status = "pending";
		});
		builder.addCase(editSkill.fulfilled, (state) => {
			state.editJobDialog.isOpen = false;
			state.editSkill.status = "fulfilled";
		});
		builder.addCase(editSkill.rejected, (state, action: any) => {
			if (action?.payload?.status === 429) {
				state.editSkill.status = "error";
			} else {
				state.editSkill.status = "error";
				state.editSkill.errorMessage = action.payload ?? "An error occurred";
			}
		});
		builder.addCase(fetchMaxWorkersByLocationJob.pending, (state) => {
			state.fetchMaxWorkersByLocationJob.status = "pending";
		});
		builder.addCase(fetchMaxWorkersByLocationJob.fulfilled, (state, action) => {
			// The legacy JS thunk shares this action type and fulfils with the
			// bare count, so accept both shapes
			const payload: any = action.payload;
			const isWithWorkers =
				!!payload && typeof payload === "object" && "count" in payload;
			state.fetchMaxWorkersByLocationJob.status = "fulfilled";
			state.fetchMaxWorkersByLocationJob.data = isWithWorkers
				? payload.count
				: payload;
			state.fetchMaxWorkersByLocationJob.workers = isWithWorkers
				? payload.workers
				: [];
		});
		builder.addCase(
			fetchMaxWorkersByLocationJob.rejected,
			(state, action: any) => {
				if (action?.payload?.status === 429) {
					state.fetchMaxWorkersByLocationJob.status = "error";
				} else {
					state.fetchMaxWorkersByLocationJob.status = "error";
					state.fetchMaxWorkersByLocationJob.errorMessage =
						action.payload ?? "An error occurred";
				}
			}
		);
		builder.addCase(getWorkersCountByShift.pending, (state) => {
			state.getWorkersCountByShift.status = "pending";
		});
		builder.addCase(getWorkersCountByShift.fulfilled, (state, action) => {
			const shiftId = action.payload.shiftId;
			const count = action.payload.count;
			state.getWorkersCountByShift.data[shiftId] = count;
			state.getWorkersCountByShift.status = "fulfilled";
		});
		builder.addCase(getWorkersCountByShift.rejected, (state, action: any) => {
			if (action?.payload?.status === 429) {
				state.getWorkersCountByShift.status = "error";
			} else {
				state.getWorkersCountByShift.status = "error";
				state.getWorkersCountByShift.errorMessage =
					action.payload ?? "An error occurred";
			}
		});
		// [createNewJob.fulfilled]: (state, action) => {
		// 	state.allJobs = [...state.allJobs, action.payload];
		// 	state.addJobDialog.isOpen = false;
		// 	state.createNewJob.status = "fulfilled";
		// },
		// [createNewJob.pending]: (state) => {
		// 	state.createNewJob.status = "pending";
		// },
		// [createNewJob.rejected]: (state, action) => {
		// 	state.createNewJob.status = "error";
		// 	state.createNewJob.errorMessage = action.payload ?? "An error occurred";
		// },
		// [fetchJobsByUserId.fulfilled]: (state, action) => {
		// 	state.allJobs = action.payload.jobs;
		// 	state.allSkills = action.payload.skills;
		// 	state.fetchJobsByUserId.status = "fulfilled";
		// },
		// [fetchJobsByUserId.pending]: (state) => {
		// 	state.fetchJobsByUserId.status = "pending";
		// },
		// [fetchJobsByUserId.rejected]: (state, action) => {
		// 	state.fetchJobsByUserId.status = "error";
		// 	state.fetchJobsByUserId.errorMessage =
		// 		action.payload ?? "An error occurred";
		// },

		// [editJob.fulfilled]: (state, action) => {
		// 	const jobId = action.payload?.jobId;
		// 	const newWorkerData = action.payload?.data;
		// 	let index = state.allJobs.findIndex((el) => el.id === jobId);
		// 	state.allJobs[index] = newWorkerData;
		// 	state.editJobDialog.isOpen = false;
		// 	state.editJob.status = "fulfilled";
		// },
		// [editJob.pending]: (state) => {
		// 	state.editJob.status = "pending";
		// },
		// [editJob.rejected]: (state, action) => {
		// 	state.editJob.status = "error";
		// 	state.editJob.errorMessage = action.payload ?? "An error occurred";
		// },

		// [createNewSkill.fulfilled]: (state, action) => {
		// 	state.allSkills = [...state.allSkills, action.payload];
		// 	state.createSkillDialog.isOpen = false;
		// 	state.createNewSkill.status = "fulfilled";
		// },
		// [createNewSkill.pending]: (state) => {
		// 	state.createNewSkill.status = "pending";
		// },
		// [createNewSkill.rejected]: (state, action) => {
		// 	state.createNewSkill.status = "error";
		// 	state.createNewSkill.errorMessage = action.payload ?? "An error occurred";
		// },

		// [editSkill.fulfilled]: (state) => {
		// 	state.editJobDialog.isOpen = false;
		// 	state.editSkill.status = "fulfilled";
		// },
		// [editSkill.pending]: (state) => {
		// 	state.editSkill.status = "pending";
		// },
		// [editSkill.rejected]: (state, action) => {
		// 	state.editSkill.status = "error";
		// 	state.editSkill.errorMessage = action.payload ?? "An error occurred";
		// },

		// [fetchMaxWorkersByLocationJob.fulfilled]: (state, action) => {
		// 	state.fetchMaxWorkersByLocationJob.status = "fulfilled";
		// 	state.fetchMaxWorkersByLocationJob.data = action.payload;
		// },
		// [fetchMaxWorkersByLocationJob.pending]: (state) => {
		// 	state.fetchMaxWorkersByLocationJob.status = "pending";
		// },
		// [fetchMaxWorkersByLocationJob.rejected]: (state, action) => {
		// 	state.fetchMaxWorkersByLocationJob.status = "error";
		// 	state.fetchMaxWorkersByLocationJob.errorMessage =
		// 		action.payload ?? "An error occurred";
		// },
	},
});

export const {
	openAddJobDialog,
	closeAddJobDialog,
	openEditSkillDialog,
	openEditJobDialog,
	closeEditJobDialog,
	closeEditSkillDialog,
	openCreateSkillDialog,
	closeCreateSkillDialog,
	resetEditJob,
	resetCreateNewJob,
	resetCreateNewSkill,
	resetEditSkill,
	resetMaxWorkersByLocationJob,
	resetFetchJobsByUserId,
	resetWorkersCountByShift,
	openJobDetailsDrawer,
	closeJobDetailsDrawer,
	resetAdoptJob,
	setAdoptJobStep,
} = ManageJobsSlice.actions;

export default ManageJobsSlice.reducer;
